import type { ReactNode } from 'react';
import React, { isValidElement } from 'react';
import { Box, Stack, ScrollArea } from '@mantine/core';

import { Split } from '@ayocore/platform/client/components/Split';

const boxStyle = {
  display: 'flex' as const,
  flexDirection: 'column' as const,
  minHeight: 0,
  flex: 1,
};

const scrollAreaStyles = {
  viewport: { paddingInlineEnd: 10, paddingBottom: 0 },
};

export interface ViewLayoutStackProps {
  children?: ReactNode;
}

/**
 * Стек-панель внутри ViewLayout. Используется только как дочерний элемент ViewLayout.
 * При двух Stack и storageKey — первый становится левой панелью (со скроллом), второй — правой.
 */
export function ViewLayoutStack({ children }: ViewLayoutStackProps) {
  return <>{children}</>;
}

function isViewLayoutStack(
  child: React.ReactNode
): child is React.ReactElement<ViewLayoutStackProps> {
  return isValidElement(child) && child.type === ViewLayoutStack;
}

export interface ViewLayoutSplitProps {
  defaultLeftPercent?: number;
  minPercent?: number;
  maxPercent?: number;
}

export interface ViewLayoutProps {
  /** Ключ для сохранения пропорций split в localStorage. При заданном ключе и двух Stack — рендерится Split. */
  storageKey?: string;
  /** Пропорции split (только при storageKey). */
  splitProps?: ViewLayoutSplitProps;
  children?: ReactNode;
}

/**
 * ViewLayout — верхний layout для entity view (PartnerView, DealView, LeadView).
 * Задаёт общую структуру: flex-колонка, ScrollArea, отступы контента.
 *
 * Режимы:
 * - Без storageKey или один ViewLayout.Stack — одна колонка со скроллом.
 * - storageKey + два ViewLayout.Stack — Split с ресайзером между панелями.
 */
export function ViewLayout({ storageKey, splitProps, children }: ViewLayoutProps) {
  const childArray = React.Children.toArray(children);
  const stacks = childArray.filter(isViewLayoutStack);

  const hasSplit = Boolean(storageKey && stacks.length === 2);

  if (hasSplit && storageKey) {
    const [leftContent, rightContent] = stacks.map(s => s.props.children);
    return (
      <Box h='100%' style={boxStyle}>
        <Split
          storageKey={storageKey}
          defaultLeftPercent={splitProps?.defaultLeftPercent}
          minPercent={splitProps?.minPercent}
          maxPercent={splitProps?.maxPercent}
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
          }}
        >
          <Split.Pane>
            <Stack h='100%' style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <ScrollArea
                style={{ flex: 1, minHeight: 0 }}
                offsetScrollbars
                scrollbarSize={6}
                scrollHideDelay={0}
                styles={scrollAreaStyles}
              >
                <Stack gap='xs' pb='xs'>
                  {leftContent}
                </Stack>
              </ScrollArea>
            </Stack>
          </Split.Pane>
          <Split.Resizer />
          <Split.Pane>
            <Stack h='100%' gap='xs' pb='xs' style={{ minHeight: 0, overflow: 'auto' }}>
              {rightContent}
            </Stack>
          </Split.Pane>
        </Split>
      </Box>
    );
  }

  // Одна колонка: либо один Stack, либо произвольные children
  const content = stacks.length === 1 ? stacks[0].props.children : children;

  return (
    <Box h='100%' style={boxStyle}>
      <Stack h='100%' style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <ScrollArea
          style={{ flex: 1, minHeight: 0 }}
          offsetScrollbars
          scrollbarSize={6}
          scrollHideDelay={0}
          styles={scrollAreaStyles}
        >
          <Stack gap='xs' pb='xs'>
            {content}
          </Stack>
        </ScrollArea>
      </Stack>
    </Box>
  );
}

ViewLayout.Stack = ViewLayoutStack;
